<template>
  <label
    class="relative text-sm"
    :class="[
      $style.wrapper,
      $attrs.disabled && $style.disabled,
      (help || $slots.help) && $style.multirow,
      small ? $style.small : $style.large,
      $attrs.class,
    ]"
    @click.stop
  >
    <input
      :class="[$style.input, 'sr-only']"
      v-bind="inputAttrs"
      :checked="modelValue"
      type="checkbox"
      @input="toggle"
    />
    <span :class="[$style.toggle, modelValue && 'bg-form-selected-background after:left-1/2']" />
    <span v-if="label || $slots.label" :class="$style.label" class="h-max text-sm">
      <slot name="label">{{ label }}</slot>
    </span>
    <span v-if="help || $slots.help" class="text-xs text-gray-60" :class="$style.help">
      <slot name="help">{{ help }}</slot>
    </span>
  </label>
</template>

<script setup lang="ts">
import { computed, useAttrs } from 'vue'
import { omit } from 'lodash-es'

withDefaults(
  defineProps<{
    modelValue?: boolean
    /**
     * label text
     */
    label?: string
    /**
     * help text
     */
    help?: string
    small?: boolean
  }>(),
  {
    modelValue: false,
    label: '',
    help: '',
    small: false,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

defineOptions({
  inheritAttrs: false,
})

const attrs = useAttrs()
const inputAttrs = computed(() => omit(attrs, 'class'))

const toggle = (event: Event) => {
  if (!(event.target instanceof HTMLInputElement)) return
  emit('update:modelValue', event.target.checked)
}
</script>

<style lang="scss" module>
.wrapper {
  display: grid;
  align-items: center;
  justify-content: center;
  grid-template-areas:
    'toggle label'
    'toggle help';
  grid-template-columns: 34px 1fr;
  grid-template-rows: 24px auto;
  column-gap: 12px;
}

.multirow {
  row-gap: 4px;
}

.disabled {
  cursor: not-allowed;
  color: var(--gray-50);
}

.toggle {
  position: relative;
  display: flex;
  align-items: center;
  height: 1.25rem;
  width: 2.25rem;
  cursor: pointer;
  background: var(--gray-40);
  user-select: none;
  border-radius: 2rem;
  padding: 3px;
  transition: all 0.4s ease;

  &::after {
    content: '';
    display: block;
    position: relative;
    left: 0;
    width: 0.875rem;
    height: 0.875rem;
    border-radius: 50%;
    background-color: var(--white);
    transition: left ease-out 0.2s;
  }
}

.input:disabled + .toggle {
  cursor: not-allowed;
  background: var(--gray-30);

  &::after {
    background: var(--gray-10);
  }
}
.input:disabled ~ .label {
  color: var(--gray-60);
  cursor: not-allowed;
}

.label {
  grid-area: label;
  font-weight: theme('fontWeight.medium');
  align-self: center;
  font-size: theme('fontSize.sm');
}

.help {
  grid-area: help;
}

.large {
  grid-template-columns: 40px 1fr;
  column-gap: 12px;

  .toggle {
    height: 24px;
    width: 40px;

    &::after {
      width: 18px;
      height: 18px;
    }
  }
}

.small {
  grid-template-columns: 28px 1fr;
  column-gap: 8px;

  .toggle {
    height: 16px;
    width: 28px;

    &::after {
      width: 12px;
      height: 12px;
    }
  }
}
</style>
